import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  service: "",
  property_address: "",
  property_size: "",
  message: "",
};

export default function LeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.phone || !form.service || !form.property_address || !form.property_size) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Quote request received. We'll be in touch within 24 hours.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="leadform-section" className="relative py-24 md:py-32 bg-[#070F1C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
        <div className="lg:col-span-2">
          <div className="hairline mb-6">Free Quote</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Tell us about
            <span className="text-[#C9A24B]"> your property.</span>
          </h2>
          <p className="mt-6 text-white/70 text-base leading-relaxed max-w-md">
            Share a few details and our team will send back a clear, no-pressure quote — usually within 24 hours.
          </p>
          <ul className="mt-10 space-y-4">
            {[
              "No-obligation quotes, always",
              "Flat seasonal pricing — no surprises",
              "Fully insured & WSIB compliant",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-white/80">
                <CheckCircle2 size={18} className="text-[#C9A24B] mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="leadform"
          className="lg:col-span-3 bg-[#0B1B3D] border border-white/10 p-8 md:p-10"
        >
          {done ? (
            <div data-testid="leadform-success" className="py-16 text-center">
              <CheckCircle2 size={48} className="text-[#C9A24B] mx-auto mb-6" />
              <h3 className="font-display text-2xl font-semibold mb-3">Request received.</h3>
              <p className="text-white/70 text-sm max-w-sm mx-auto">
                Thanks — we'll reach out shortly with your quote. Keep an eye on your inbox.
              </p>
              <button
                type="button"
                onClick={() => setDone(false)}
                data-testid="leadform-reset"
                className="mt-8 text-sm text-[#C9A24B] hover:text-[#E0B962] underline underline-offset-4"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field id="name" label="Full name *">
                  <Input
                    id="name"
                    data-testid="leadform-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Doe"
                    className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                  />
                </Field>
                <Field id="email" label="Email *">
                  <Input
                    id="email"
                    type="email"
                    data-testid="leadform-email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@example.com"
                    className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                  />
                </Field>
                <Field id="phone" label="Phone *">
                  <Input
                    id="phone"
                    data-testid="leadform-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(613) 555-0123"
                    className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                  />
                </Field>
                <Field id="service" label="Service needed *">
                  <Select
                    value={form.service}
                    onValueChange={(v) => update("service", v)}
                  >
                    <SelectTrigger
                      id="service"
                      data-testid="leadform-service"
                      className="bg-[#050B14] border-white/15 text-white rounded-none h-12 focus:ring-[#C9A24B] focus:ring-1"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B1B3D] border-white/15 text-white rounded-none">
                      <SelectItem value="snow_removal" data-testid="service-option-snow_removal">
                        Residential Snow Removal
                      </SelectItem>
                      <SelectItem value="salting_deicing" data-testid="service-option-salting_deicing">
                        Salting & De-Icing
                      </SelectItem>
                      <SelectItem value="driveway_sealing" data-testid="service-option-driveway_sealing">
                        Driveway Sealing
                      </SelectItem>
                      <SelectItem value="other" data-testid="service-option-other">
                        Something else
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="property_address" label="Property address *" full>
                  <Input
                    id="property_address"
                    data-testid="leadform-address"
                    value={form.property_address}
                    onChange={(e) => update("property_address", e.target.value)}
                    placeholder="123 Bank St, Ottawa, ON"
                    className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                  />
                </Field>
                <Field id="property_size" label="Property / driveway size *" full>
                  <Input
                    id="property_size"
                    data-testid="leadform-size"
                    value={form.property_size}
                    onChange={(e) => update("property_size", e.target.value)}
                    placeholder="e.g. 2-car driveway, 500 sq ft, 100ft frontage"
                    className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none h-12 focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                  />
                </Field>
                <Field id="message" label="Additional details (optional)" full>
                  <Textarea
                    id="message"
                    data-testid="leadform-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Anything we should know — gate codes, steep slope, sensitive landscaping…"
                    className="bg-[#050B14] border-white/15 text-white placeholder:text-white/30 rounded-none focus-visible:ring-[#C9A24B] focus-visible:ring-1"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="leadform-submit"
                className="mt-8 group inline-flex w-full md:w-auto items-center justify-center gap-2 px-8 py-4 bg-[#C9A24B] hover:bg-[#E0B962] disabled:opacity-60 disabled:cursor-not-allowed text-[#050B14] font-semibold text-sm tracking-wide transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending request…
                  </>
                ) : (
                  <>
                    Send My Quote Request
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              <p className="mt-5 text-xs text-white/40">
                By submitting, you agree we may contact you about your request. We never share your information.
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ id, label, full, children }) {
  return (
    <div className={`space-y-2 ${full ? "md:col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
